import { errorService } from "../../../../../comun/services/ErrorService";
import {
  fx_createOlvidePasswordRequest,
  OlvidePasswordRequest,
} from "../../../../../modulos/seguridad/password/request/OlvidePasswordRequest";
import {
  parsePasswordResetError,
  passwordResetErrorMessage,
} from "../../../../../modulos/seguridad/password/response/PasswordResetErrorResponse";
import { olvidePasswordGrpcService as olvidePasswordService } from "../../../../../modulos/seguridad/password/services/OlvidePasswordGrpcService";
import { CardConnectRender } from "../../../../base/card/CardConnectRender";
import { CardBody } from "../../../../definitions/card/CardBody";
import { CardFooter } from "../../../../definitions/card/CardFooter";
import { CardHeader } from "../../../../definitions/card/CardHeader";
import { TextConfig } from "../../../../definitions/form/inputs/config/TextConfig";
import { TextField } from "../../../../implementations/form/text/TextField";

/** Mensaje del paso 1. Se muestra SIEMPRE, exista o no la cuenta: el endpoint
 *  responde 200 incondicionalmente para no revelar qué emails están
 *  registrados, así que la UI tampoco puede condicionarlo. */
const MENSAJE_CODIGO_ENVIADO =
  "Si el email está registrado, te enviamos un código";

export type OlvidePasswordCardConfig = {
  /** Email inicial (viene precargado del formulario de ingreso). */
  email: string;
  /** El servidor aceptó la solicitud: hay que pasar al paso 2. */
  onCodigoEnviado: (email: string) => void;
  /** El usuario abandonó el flujo (botón back del header). */
  onCancel: () => void;
};

/** Paso 1 del flujo "olvidé mi contraseña": pide el email y dispara el envío
 *  del código OTP. */
export class OlvidePasswordCardRender extends CardConnectRender {
  private readonly _config: OlvidePasswordCardConfig;
  private readonly _request: OlvidePasswordRequest;
  private readonly _emailField: TextField<OlvidePasswordRequest>;

  constructor(config: OlvidePasswordCardConfig) {
    super();
    this._config = config;
    this._request = fx_createOlvidePasswordRequest(config.email);
    this._emailField = this._createEmailField();
    this._backAction = () => this._config.onCancel();
  }

  protected getCardHeader(): CardHeader {
    return {
      title: "Recuperar contraseña",
      subtitle: "Te enviamos un código de 6 dígitos para volver a entrar",
    };
  }

  protected getCardBody(): CardBody {
    return {
      additionalCssClass: "",
      content: this._emailField.render(),
    };
  }

  protected getCardFooter(): CardFooter | null {
    return {
      additionalCssClass: "",
      content: `<button class="sx-btn sx-btn-primary sx-btn-block" type="button" id="olvide-submit-${this._cardId}">Enviar código</button>`,
    };
  }

  protected afterConnect(): void {
    this._emailField.connect();

    this._submitButton = document.getElementById(
      `olvide-submit-${this._cardId}`,
    ) as HTMLButtonElement;
    this._submitButton.addEventListener("click", this._onSubmitClick);
  }

  protected beforeDispose(): void {
    this._submitButton?.removeEventListener("click", this._onSubmitClick);
    this._emailField.dispose();
  }

  private _submitButton: HTMLButtonElement | null = null;
  /** Mensaje de error devuelto por el servidor para el campo (se limpia al
   *  editar). Lo publica `validation.custom` del `TextField`. */
  private _serverError: string | null = null;

  /** Campo de email; su `custom` expone el error del servidor hasta que el
   *  usuario toca el valor (`setOnChange` corre antes del re-render del error). */
  private _createEmailField(): TextField<OlvidePasswordRequest> {
    const config: TextConfig<OlvidePasswordRequest> = {
      get(request) {
        return request.email;
      },
      set(request, newValue) {
        request.email = newValue;
      },
      label: "Email",
      type: "email",
      description: "Email con el que ingresás al sistema",
      icon: "ph-envelope",
      autocomplete: "email",
    };
    const field = new TextField<OlvidePasswordRequest>(this._request, config, {
      isRequired: true,
      custom: () => this._serverError,
    });
    field.setOnChange(() => {
      this._serverError = null;
    });
    return field;
  }

  private _onSubmitClick = (): void => {
    this._emailField.showError();
    if (this._emailField.getError()) {
      this._emailField.focus();
      return;
    }

    this._setBusy(true);
    olvidePasswordService
      .sendAsync(this._request)
      .then(() => {
        errorService.addSuccess("Revisá tu correo", MENSAJE_CODIGO_ENVIADO);
        this._config.onCodigoEnviado(this._request.email);
      })
      .catch((error: unknown) => {
        this._handleError(error);
        this._setBusy(false);
      });
  };

  /** Ramifica por `code`, nunca por `message`. El único código propio del paso
   *  es `EMAIL_INVALIDO`; cualquier otra cosa (red, 500) va a un toast. */
  private _handleError(error: unknown): void {
    const parsed = parsePasswordResetError(error);
    if (parsed?.code === "EMAIL_INVALIDO") {
      this._serverError = parsed.message;
      this._emailField.showError();
      this._emailField.focus();
      return;
    }

    errorService.addError(
      "No se pudo enviar el código",
      parsed?.message ?? passwordResetErrorMessage(error),
    );
  }

  private _setBusy(isBusy: boolean): void {
    if (this._submitButton) this._submitButton.disabled = isBusy;
    if (isBusy) this._emailField.disabled();
    else this._emailField.enabled();
  }
}
